import React from 'react';
import { Action, Store } from '@reduxjs/toolkit';
import { app, getActivePolygonKey, getDifferencesLoadedSelector, inspection } from 'store';
import {
  DocumentTypes,
  ZoneCustomData,
  AnnotationCustomData,
  InspectedAnnotationCustomData,
  PDFTronTools,
  CropZoneCustomData,
  ManualAlignZoneCustomData,
  PolygonZoneCustomData,
  RepeatAnnotationCustomData,
  MarkupZoneCustomData,
} from 'types';
import { isInspectionUrl, isReportsUrl, isResultUrl } from 'utils/location';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { PDFManagerFactory } from 'pdftron';
import { updateDifference } from 'store/request/differences/actions';
import PDFAnnotationManager from './PDFAnnotationManager';
import { zoneSearchEventHandler } from './ZoneSearchEventHandler';
import PDFTronManager from './PDFTronManager';
import { AnnotationAction } from './Utils';
import {
  getAllPageCropAndPolygonZones,
  manageZoneLocking,
} from 'pdftron/docManager/AnnotationTools/utils/CropToolUtils';
import { queryClient } from 'queryClient';
import { CompactMatch } from 'store/queries/inspections/useTextMatches';

const normalizeCompactMatchSide = (coords: CompactMatch[0]) => {
  const left = Math.min(coords[1], coords[3]);
  const right = Math.max(coords[1], coords[3]);
  const top = Math.min(coords[2], coords[4]);
  const bottom = Math.max(coords[2], coords[4]);

  return {
    pageNumber: coords[0],
    left,
    right,
    top,
    bottom,
  };
};

const handleAnnotationChanged = async (
  dispatch: React.Dispatch<Action>,
  documentType: DocumentTypes,
  annotations: Core.Annotations.Annotation[],
  action: string,
  store: Store,
) => {
  // Handle both rectangular crop zones and polygon crop zones
  const isRectangularCrop = annotations[0].getCustomData(CropZoneCustomData.cropZoneAnnotation) === 'true';
  const isPolygonCrop =
    annotations[0].getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true' &&
    annotations[0].getCustomData(PolygonZoneCustomData.shapePolygon) !== 'true';
  const isProgrammaticRedraw = annotations[0].getCustomData(AnnotationCustomData.drawMarkups) === 'true';

  if (action === AnnotationAction.ADD && (isRectangularCrop || isPolygonCrop)) {
    const annotManager = PDFAnnotationManager.getInstance(documentType);

    // Prevent pdftron bug when moving crop zone to another page
    const expectedTool = isRectangularCrop ? PDFTronTools.CROP : PDFTronTools.POLYGON;
    if (annotations[0].ToolName !== expectedTool) {
      annotManager?.deleteAnnotation(annotations[0]);
      return;
    }

    // Check for active polygon shapes and show warning dialog (only for user-initiated adds)
    if (!isProgrammaticRedraw) {
      const keys = getActivePolygonKey(store.getState(), documentType);
      if (keys !== '') {
        store.dispatch(inspection.actions.setPendingCropZone({ documentType, pendingCropId: annotations[0].Id }));
      }
    }
  }

  // Find the annotation with deletion marker - it may not be the first in the array
  const deletionMarkerAnnotation = annotations.find(
    (anno) => anno?.getCustomData(AnnotationCustomData.deletionMarker) === 'true',
  );
  const isDeletionMarker = Boolean(deletionMarkerAnnotation);
  const markerDifferenceType = annotations[0].getCustomData('differenceType');
  const isMissingContentMarker = markerDifferenceType === 'Deletion' || markerDifferenceType === 'Insertion';
  const isLiveText = annotations[0].getCustomData(AnnotationCustomData.liveText) === 'true';
  if (isLiveText) return;
  // we don't want to update the annotations if we are in the result page or report page. With the exception of missing content markers
  if ((isResultUrl() && !isDeletionMarker && !isMissingContentMarker) || isReportsUrl()) return;

  if (annotations.some((anno) => anno.getCustomData(ZoneCustomData.zoneSearch) === 'true')) {
    zoneSearchEventHandler(action, annotations, store, dispatch, documentType);
  }

  const instance = PDFTronManager.getPDFInstance(documentType);
  if (!instance) return;

  // check if page has crop/polygon zones, if so, check if they need to be locked (if they contain annotations)
  const cropAndPolygonZones = getAllPageCropAndPolygonZones(instance, annotations[0].PageNumber);
  cropAndPolygonZones.forEach((zone) => {
    manageZoneLocking(zone, documentType);
  });

  const newAnnotation = annotations[0];

  const inputAnnotations = PDFAnnotationManager.getInputAnnotations(documentType);
  // this prevents inputAnnotations from being null (typescript error)
  if (!inputAnnotations) return;

  if ((isDeletionMarker || isMissingContentMarker) && action === AnnotationAction.MODIFY) {
    let differenceId: string;
    // Use the deletion marker annotation for position data when available
    const markerAnnotation = deletionMarkerAnnotation || newAnnotation;
    // Check if this is an embedded image deletion marker
    const isEmbeddedImageMarker = Boolean(markerAnnotation.getCustomData(AnnotationCustomData.embeddedImageId));

    if (isMissingContentMarker) {
      // New MissingContentMarkerAnnotation system - annotation ID is the difference ID
      differenceId = newAnnotation.Id;
    } else {
      // Legacy overlay system for UnmatchedAnnotation/EmbeddedImage
      // By the time this happens, what we are seeing as the Annotation is actually the High contrast overlay annotation.
      // That annotation, contains a refAnnotationId array which links it to any other annotation grouped with the overlay.
      const refAnnotationIds = (markerAnnotation.getCustomData(AnnotationCustomData.refAnnotationIds) ?? '').split(',');
      differenceId = refAnnotationIds[0];

      // For deletions, check target document; for insertions, check source document
      // For embedded images, use the document type where the event occurred
      const targetDocType = isEmbeddedImageMarker
        ? documentType
        : markerDifferenceType === 'Insertion'
          ? DocumentTypes.source
          : DocumentTypes.target;
      const annotManager = PDFAnnotationManager.getInstance(targetDocType);
      const annot = annotManager?.getAnnotationById(refAnnotationIds[0]);
      if (annot?.PageNumber !== markerAnnotation.PageNumber) {
        annotManager?.deleteAnnotation(markerAnnotation); // PDFTron makes a new annot on the new page, we need to delete it. A new marker is drawn in drawDifferences
      }
    }

    // Get page height from the appropriate document
    // For embedded images, use the document type where the marker is (documentType parameter)
    // For regular deletions/insertions, use target for deletions, source for insertions
    const relevantDocType = isEmbeddedImageMarker
      ? documentType
      : markerDifferenceType === 'Insertion'
        ? DocumentTypes.source
        : DocumentTypes.target;
    const updatePayload = {
      deletionMarkPosition: {
        // Keep field name for backward compatibility
        x: markerAnnotation.X,
        y: markerAnnotation.Y,
        pageHeight: PDFManagerFactory.getViewer(relevantDocType)?.getPageSize(markerAnnotation.PageNumber).height || 0,
        pageNumber: markerAnnotation.PageNumber,
        // For embedded images, explicitly indicate which document the marker is on
        markerTarget: isEmbeddedImageMarker ? documentType : undefined,
      },
    };
    dispatch(updateDifference(differenceId, updatePayload));
    dispatch(app.actions.clearSnackMessageType('info'));
  }
};

const handleAnnotationLoaded = (
  dispatch: React.Dispatch<Action>,
  documentType: DocumentTypes,
  docViewer: Core.DocumentViewer,
  store: Store,
) => {
  const annotationManager = docViewer.getAnnotationManager();
  PDFAnnotationManager.setInstance(documentType, annotationManager);

  // Listen for new annotations
  annotationManager.addEventListener(
    'annotationChanged' as unknown as any, // TODO-@PDFTRON Fix this hack when PDFTron types are fine
    (annotations: Core.Annotations.Annotation[], action: string) => {
      handleAnnotationChanged(dispatch, documentType, annotations, action, store);
    },
  );
};

const handleAnnotationSelected = (
  annotations: Core.Annotations.Annotation[],
  store: Store,
  document: DocumentTypes,
) => {
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  if (!pdfDocManager) return;

  // If the user has selected the loading indicator, do not continue.
  if (annotations[0].Id === 'loader') return;

  // Internal annotations should not show popups or be interactable - deselect them
  if (isInspectionUrl() && !pdfDocManager.isGVAnnotation(annotations[0])) {
    const annoManager = PDFAnnotationManager.getInstance(document);
    annoManager?.deselectAnnotation(annotations[0]);
    pdfDocManager[document]?.instance.UI.annotationPopup.update([]);
    return;
  }

  // reset the popup menu each time annotation is selected
  if (isResultUrl()) {
    const selectedAnnotation = annotations[0];
    const groupId = selectedAnnotation.getCustomData('groupId');
    const isMarkupAnnotation = selectedAnnotation.getCustomData(MarkupZoneCustomData.markupZoneAnnotation) === 'true';
    const isColorPointAnnotation = selectedAnnotation.getCustomData('colorPickerAnnotation') === 'true';
    const isEmbeddedImageDeletionMarker =
      selectedAnnotation.getCustomData(AnnotationCustomData.deletionMarker) === 'true' &&
      !!selectedAnnotation.getCustomData(AnnotationCustomData.embeddedImageId);

    if (isEmbeddedImageDeletionMarker) {
      const embeddedImageId = selectedAnnotation.getCustomData(AnnotationCustomData.embeddedImageId);
      const popup = pdfDocManager.getDrawMatchPopup(embeddedImageId, document);
      pdfDocManager[document]?.instance.UI.annotationPopup.update([popup]);
    } else if (!groupId && !isMarkupAnnotation && !isColorPointAnnotation) {
      // clearing the annotation popup for grouped differences has been causing some issues VE-8395
      // also don't clear for color picker annotations to avoid race condition with their popup updates
      pdfDocManager[document]?.instance.UI.annotationPopup.update([]);
    }
  }

  // inspected annotations need to have hovering capacity but not selection
  if (annotations[0].getCustomData(InspectedAnnotationCustomData.inspectedAnnotation) === 'true') {
    const annoManager = PDFAnnotationManager.getInstance(document);
    annoManager?.deselectAnnotation(annotations[0]);
    return;
  }

  if (annotations[0].ToolName === PDFTronTools.SHIFTED_GRAPHIC) {
    const annoManager = PDFAnnotationManager.getInstance(document);
    annoManager?.deselectAnnotation(annotations[0]);

    pdfDocManager[document]?.instance.UI.annotationPopup.update([]);
    return;
  }

  if (isResultUrl() && annotations[0].getCustomData(RepeatAnnotationCustomData.repeatAnnotation) === 'true') {
    PDFAnnotationManager.repeatAnnotationSelected(annotations[0]);
  }

  const differencesLoaded = getDifferencesLoadedSelector(store.getState());
  // we only want this behavior on results page
  if (differencesLoaded && isResultUrl() && annotations[0].getCustomData(AnnotationCustomData.difference) === 'true') {
    const isMarkupAnnotation = annotations[0].getCustomData(MarkupZoneCustomData.markupZoneAnnotation) === 'true';

    if (isMarkupAnnotation) {
      PDFAnnotationManager.markupZoneAnnotationSelected(annotations[0]);
    } else {
      pdfDocManager.differenceAnnotationSelected(annotations[0], document);
    }
  }
};

const handleAnnotationDeselected = (annotation: Core.Annotations.Annotation, document: DocumentTypes) => {
  if (isInspectionUrl()) {
    // deselect all annotations when in inspection page (to make sure we dont break differences for now)
    PDFAnnotationManager.getInstance(DocumentTypes.source)?.deselectAllAnnotations();
    PDFAnnotationManager.getInstance(DocumentTypes.target)?.deselectAllAnnotations();
  }
  const isCropZone = annotation.ToolName === PDFTronTools.CROP;
  const isManualAlignZone = annotation.getCustomData?.(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';
  if (isCropZone || isManualAlignZone) {
    PDFManagerFactory?.getPDFDocManager()?.addCropOverlay(document, annotation);
    return;
  }
};

const handleTextMatchClick = (
  event: MouseEvent,
  documentType: DocumentTypes,
  instance: WebViewerInstance,
  inspectionId: string,
  textMatchJobId: string,
  cdnPathPrefix?: string | null,
) => {
  if (!event.ctrlKey && !event.metaKey) return;
  if (!isResultUrl()) return;

  const documentViewer = instance.Core.documentViewer;
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();

  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollViewRect = scrollViewElement.getBoundingClientRect();
  const clickX = event.clientX - scrollViewRect.left + scrollViewElement.scrollLeft;
  const clickY = event.clientY - scrollViewRect.top + scrollViewElement.scrollTop;

  const pages = displayMode.getSelectedPages({ x: clickX, y: clickY }, { x: clickX, y: clickY });
  if (!pages || !pages.first) return;

  const pageNumber = pages.first;
  const pageCoords = displayMode.windowToPage({ x: clickX, y: clickY }, pageNumber);

  // Look up compact matches for this page from React Query cache
  const side = documentType === DocumentTypes.source ? 'master' : 'sample';
  const matches = queryClient.getQueryData<CompactMatch[]>([
    'textMatchPage',
    inspectionId,
    textMatchJobId,
    side,
    pageNumber,
    cdnPathPrefix,
  ]);

  if (!matches || matches.length === 0) return;

  // Compact format: [[mPage, mLeft, mTop, mRight, mBottom], [sPage, sLeft, sTop, sRight, sBottom]]
  const clickedIdx = side === 'master' ? 0 : 1;
  const targetIdx = side === 'master' ? 1 : 0;

  const match = matches.find((m) => {
    const normalized = normalizeCompactMatchSide(m[clickedIdx]);
    return (
      pageCoords.x >= normalized.left &&
      pageCoords.x <= normalized.right &&
      pageCoords.y >= normalized.top &&
      pageCoords.y <= normalized.bottom
    );
  });

  if (!match) return;

  const clicked = match[clickedIdx];
  const target = match[targetIdx];
  const normalizedClicked = normalizeCompactMatchSide(clicked);
  const normalizedTarget = normalizeCompactMatchSide(target);
  const targetDocument = documentType === DocumentTypes.source ? DocumentTypes.target : DocumentTypes.source;

  PDFAnnotationManager.createTempTextMatchHighlight(
    {
      documentType,
      location: {
        pageNumber: normalizedClicked.pageNumber,
        boundingBox: {
          left: normalizedClicked.left,
          top: normalizedClicked.top,
          right: normalizedClicked.right,
          bottom: normalizedClicked.bottom,
        },
      },
    },
    {
      documentType: targetDocument,
      location: {
        pageNumber: normalizedTarget.pageNumber,
        boundingBox: {
          left: normalizedTarget.left,
          top: normalizedTarget.top,
          right: normalizedTarget.right,
          bottom: normalizedTarget.bottom,
        },
      },
    },
  );
};

export {
  handleAnnotationChanged,
  handleAnnotationLoaded,
  handleAnnotationSelected,
  handleAnnotationDeselected,
  handleTextMatchClick,
};
